import { Injectable, computed, effect, inject, signal } from '@angular/core'
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http'
import { CanActivateFn, NavigationEnd, Router } from '@angular/router'
import { MessageService } from '@openng/optimus-ui/api'
import { filter } from 'rxjs/operators'
import { FuseqHeaders } from '../../FuseQ/fuseq.headers'
import { FuseqSessionService } from '../../FuseQ/session.service'
import { FuseqRaidenService } from '../../FuseQ/fuseq-raiden/fuseqRaiden.service'
import { environment } from '../../../environments/environment'

/**
 * Maintenance mode "stile GitLab".
 *
 * - Polla GET /api/maintenance (pubblico) con backoff esponenziale quando il
 *   backend è giù; l'ultimo stato noto è persistito in localStorage, così
 *   durante l'offline della finestra di manutenzione l'app sa comunque di
 *   essere in maintenance (anche dopo un refresh).
 * - `phase` passa ad 'active' automaticamente all'orario di inizio di una
 *   programmazione (AUTO_START_SCHEDULED, stile GitLab). La FINE è sempre
 *   manuale: quando il backend torna e lo stato è 'off'/'scheduled' tutti
 *   vengono sbloccati al primo poll.
 * - `blockMode` è l'esito dell'enforcement: 'overlay'/'redirect' solo se
 *   phase è active, senza bypass e con display configurato.
 */

/** Le richieste marcate con questo flag non generano toast/redirect globali (poll interni). */
export const SKIP_GLOBAL_ERROR_NOTIFICATIONS = new HttpContextToken<boolean>(() => false)

/** Permission che abilitano gestione maintenance e bypass del blocco. Allineate al backend. */
export const MAINTENANCE_MANAGE_PERMISSIONS = ['MENU_QUEUE_MONITORING', 'SYSADMIN_LOCAL']

/** Se true una programmazione diventa bloccante all'orario di inizio (stile GitLab). */
const AUTO_START_SCHEDULED = true

const STORAGE_KEY = 'awp.maintenance.state'
const RETURN_URL_KEY = 'awp.maintenance.returnUrl'
const DISMISS_KEY = 'awp.maintenance.dismissed'
const NORMAL_POLL_MS = 60_000
const ACTIVE_POLL_MS = 15_000
const FIRST_ERROR_DELAY_MS = 30_000
const MAX_BACKOFF_MS = 300_000

export type MaintenanceMode = 'off' | 'scheduled' | 'active'
export type MaintenanceDisplay = 'banner' | 'overlay' | 'redirect'

export interface MaintenanceState {
  mode: MaintenanceMode
  message: string | null
  badge: string | null
  color: string | null
  display: MaintenanceDisplay
  startsAt: string | null
  endsAt: string | null
  updatedBy: string | null
  updatedAt: string | null
}

interface MaintenanceResponse extends MaintenanceState {
  serverTime?: string
  /** Presente solo nel push di reload: chiede ai client di ricaricare la pagina. */
  reload?: boolean
  /** 'maintenance' = fine manutenzione automatica; 'manual' = bottone admin. */
  reloadReason?: 'maintenance' | 'manual'
}

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly http = inject(HttpClient)
  private readonly router = inject(Router)
  private readonly fuseqHeaders = inject(FuseqHeaders)
  private readonly fuseqSession = inject(FuseqSessionService)
  private readonly fuseqRaiden = inject(FuseqRaidenService)
  private readonly messageService = inject(MessageService)

  /** Stato corrente (all'avvio: ultima copia nota da localStorage). */
  readonly state = signal<MaintenanceState | null>(this.readCache())

  /** true/false in base all'ultimo esito del poll, null se mai contattato. */
  readonly reachable = signal<boolean | null>(null)

  /** Timestamp dell'ultimo poll andato a buon fine (null = mai in questa sessione). */
  readonly lastSuccessAt = signal<number | null>(null)

  /** Offset clock server-client (ms), per countdown corretti. */
  private readonly serverSkewMs = signal(0)

  /** Tick 1s: letto solo se c'è un countdown attivo, altrimenti nessun re-render. */
  private readonly nowMs = signal(Date.now())

  /** Bump ad ogni navigazione: rivaluta il bypass dopo login/logout. */
  private readonly navTick = signal(0)

  /** updatedAt per cui il banner è stato chiuso in questa sessione browser. */
  private readonly dismissedFor = signal(this.readDismissed())

  private pollTimer: ReturnType<typeof setTimeout> | null = null
  private nextDelayMs = NORMAL_POLL_MS
  private inFlight = false
  private started = false
  private reloadScheduled = false

  constructor () {
    this.removeReloadParam()
    setInterval(() => this.nowMs.set(Date.now()), 1000)
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.navTick.update((v) => v + 1))

    // Push live dal backend (Raiden): blocco/sblocco istantaneo per i client
    // connessi; il polling HTTP resta come fallback (es. login page pre-socket).
    this.fuseqRaiden.onNewEvent().subscribe((event: any) => {
      if (event?.type === 'MAINTENANCE_UPDATED' && event?.content) {
        this.handlePush(event.content as MaintenanceResponse)
      }
    })

    // Enforcement live: al cambio stato porta fuori dall'app (se display=redirect).
    effect(() => {
      if (this.blockMode() !== 'redirect') {
        return
      }
      const currentUrl = this.router.url
      if (currentUrl.startsWith('/maintenance')) {
        return
      }
      this.rememberReturnUrl(currentUrl)
      void this.router.navigate(['/maintenance'])
    })
  }

  /** Fase effettiva: applica l'auto-start delle programmazioni. */
  readonly phase = computed<MaintenanceMode>(() => {
    const s = this.state()
    if (!s) {
      return 'off'
    }
    if (s.mode === 'scheduled' && AUTO_START_SCHEDULED && s.startsAt) {
      const start = Date.parse(s.startsAt)
      if (!Number.isNaN(start) && this.nowMs() >= start) {
        return 'active'
      }
    }
    return s.mode
  })

  readonly display = computed<MaintenanceDisplay>(() => this.state()?.display ?? 'banner')

  /** True se l'utente corrente può gestire la maintenance (e quindi bypassare il blocco). */
  readonly hasBypass = computed(() => {
    this.navTick()
    const permissions: string[] = this.fuseqSession.session?.permissions ?? []
    return MAINTENANCE_MANAGE_PERMISSIONS.some((p) => permissions.includes(p))
  })

  /** Esito enforcement: 'none' = app utilizzabile (solo banner eventuale). */
  readonly blockMode = computed<'none' | 'overlay' | 'redirect'>(() => {
    if (this.phase() !== 'active' || this.hasBypass()) {
      return 'none'
    }
    const display = this.display()
    return display === 'overlay' || display === 'redirect' ? display : 'none'
  })

  /** Banner visibile: programmazione (tutti) o maintenance attiva (bypass / display=banner). */
  readonly showBanner = computed(() => {
    const s = this.state()
    if (!s || !s.message) {
      return false
    }
    if (this.phase() === 'scheduled') {
      return true
    }
    if (this.phase() === 'active') {
      return this.hasBypass() || s.display === 'banner'
    }
    return false
  })

  readonly bannerColor = computed(() => this.state()?.color || '#D97706')

  /** Ms mancanti: verso l'inizio se programmata, verso la fine stimata se attiva. */
  readonly countdownMs = computed<number | null>(() => {
    const s = this.state()
    if (!s) {
      return null
    }
    const phase = this.phase()
    const target = phase === 'scheduled' ? s.startsAt : phase === 'active' ? s.endsAt : null
    if (!target) {
      return null
    }
    const parsed = Date.parse(target)
    if (Number.isNaN(parsed)) {
      return null
    }
    return Math.max(0, parsed - (this.nowMs() + this.serverSkewMs()))
  })

  /** Countdown formattato ('1g 02:33:10' oppure '02:33:10'), null se non richiesto. */
  formatCountdown (): string | null {
    const ms = this.countdownMs()
    if (ms === null) {
      return null
    }
    const total = Math.floor(ms / 1000)
    const days = Math.floor(total / 86400)
    const hours = Math.floor((total % 86400) / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${days > 0 ? days + 'g ' : ''}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  }

  /** Avvia polling + refresh su ritorno online/focus tab. Idempotente. */
  start (): void {
    if (this.started) {
      return
    }
    this.started = true
    this.refreshNow()
    window.addEventListener('online', () => this.refreshNow())
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.refreshNow()
      }
    })
  }

  /** Forza subito un poll (usato anche dal bottone "Riprova" della pagina maintenance). */
  refreshNow (): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
    this.poll()
  }

  /** Aggiorna lo stato sul backend (UI admin). Ritorna lo stato salvato. */
  update (payload: Partial<MaintenanceState>): Promise<MaintenanceState> {
    const endpoint = `${environment.API_URL}/maintenance`
    return new Promise((resolve, reject) => {
      this.http.put<MaintenanceResponse>(endpoint, payload, this.fuseqHeaders.setTokenHeaders('PUT')).subscribe({
        next: (data) => {
          const state = this.applyResponse(data)
          resolve(state)
        },
        error: (error) => reject(error)
      })
    })
  }

  /** URL da ripristinare allo sblocco (impostato quando si viene rediretti alla pagina). */
  returnUrl (): string {
    try {
      return sessionStorage.getItem(RETURN_URL_KEY) || '/'
    } catch {
      return '/'
    }
  }

  rememberReturnUrl (url: string): void {
    try {
      if (url && !url.startsWith('/maintenance') && !url.startsWith('/login')) {
        sessionStorage.setItem(RETURN_URL_KEY, url)
      }
    } catch { /* storage non disponibile */ }
  }

  /** Banner chiuso per sessione; si riapre automaticamente al prossimo aggiornamento. */
  readonly bannerDismissed = computed(() => {
    return !!this.dismissedFor() && this.dismissedFor() === (this.state()?.updatedAt ?? '')
  })

  dismissBanner (): void {
    const updatedAt = this.state()?.updatedAt ?? ''
    this.dismissedFor.set(updatedAt)
    try {
      sessionStorage.setItem(DISMISS_KEY, updatedAt)
    } catch { /* storage non disponibile */ }
  }

  /** Stato aggiornato via push socket: applica e riallinea la cadenza del fallback. */
  private handlePush (data: MaintenanceResponse): void {
    this.applyResponse(data)
    this.reachable.set(true)
    this.lastSuccessAt.set(Date.now())
    if (this.started) {
      this.nextDelayMs = this.phase() === 'active' ? ACTIVE_POLL_MS : NORMAL_POLL_MS
      this.schedule(this.nextDelayMs)
    }
    if (data.reload) {
      this.scheduleForcedReload(data.reloadReason)
    }
  }

  /** Chiede al backend di ricaricare la pagina su tutti i client connessi. */
  forceReloadAll (): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.post(`${environment.API_URL}/maintenance/reload`, {}, this.fuseqHeaders.setTokenHeaders('POST')).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      })
    })
  }

  /**
   * "Ctrl+F5" remoto a fine manutenzione: toast di avviso e poi reload con
   * cache-busting (garantisce il fetch di index.html fresco anche senza
   * header no-cache lato server; il param viene ripulito al bootstrap).
   * Chi è sulla pagina /maintenance viene lasciato al suo redirect pulito.
   */
  private scheduleForcedReload (reason?: 'maintenance' | 'manual'): void {
    if (this.reloadScheduled || location.pathname.startsWith('/maintenance')) {
      return
    }
    this.reloadScheduled = true
    this.messageService.add({
      severity: 'info',
      summary: 'Aggiornamento',
      detail: reason === 'manual'
        ? 'L\'applicazione verrà ricaricata a breve per un aggiornamento…'
        : 'Manutenzione terminata: aggiornamento della pagina in corso…',
      life: 4000
    })
    setTimeout(() => {
      this.hardNavigate(`${location.pathname}${location.search}${location.hash}`)
    }, 3000)
  }

  /**
   * Navigazione "hard" verso un URL interno: aggiunge il cache-busting per
   * forzare il fetch di index.html fresco (equivale a un ctrl+f5). Il param
   * viene ripulito dall'URL al bootstrap (removeReloadParam).
   */
  hardNavigate (url: string): void {
    try {
      const target = new URL(url, window.location.origin)
      target.searchParams.set('awp_reload', String(Date.now()))
      location.replace(`${target.pathname}${target.search}${target.hash}`)
    } catch {
      location.replace(url)
    }
  }

  /** Ripulisce dalla URL il param di cache-busting dopo il reload forzato. */
  private removeReloadParam (): void {
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has('awp_reload')) {
        url.searchParams.delete('awp_reload')
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
      }
    } catch { /* no-op */ }
  }

  private poll (): void {
    if (this.inFlight) {
      return
    }
    this.inFlight = true
    this.http.get<MaintenanceResponse>(`${environment.API_URL}/maintenance`, {
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_NOTIFICATIONS, true)
    }).subscribe({
      next: (data) => {
        this.inFlight = false
        this.applyResponse(data)
        this.reachable.set(true)
        this.lastSuccessAt.set(Date.now())
        this.nextDelayMs = this.phase() === 'active' ? ACTIVE_POLL_MS : NORMAL_POLL_MS
        this.schedule(this.nextDelayMs)
      },
      error: () => {
        this.inFlight = false
        this.reachable.set(false)
        this.nextDelayMs = this.nextDelayMs >= FIRST_ERROR_DELAY_MS
          ? Math.min(this.nextDelayMs * 2, MAX_BACKOFF_MS)
          : FIRST_ERROR_DELAY_MS
        this.schedule(this.nextDelayMs)
      }
    })
  }

  private applyResponse (data: MaintenanceResponse): MaintenanceState {
    const skew = data.serverTime ? Date.parse(data.serverTime) - Date.now() : 0
    this.serverSkewMs.set(Number.isNaN(skew) ? 0 : skew)
    const state: MaintenanceState = {
      mode: data.mode === 'scheduled' || data.mode === 'active' ? data.mode : 'off',
      message: data.message ?? null,
      badge: data.badge ?? null,
      color: data.color ?? null,
      display: data.display === 'overlay' || data.display === 'redirect' ? data.display : 'banner',
      startsAt: data.startsAt ?? null,
      endsAt: data.endsAt ?? null,
      updatedBy: data.updatedBy ?? null,
      updatedAt: data.updatedAt ?? null
    }
    this.state.set(state)
    this.writeCache(state)
    return state
  }

  private schedule (ms: number): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
    }
    this.pollTimer = setTimeout(() => this.poll(), ms)
  }

  private readDismissed (): string {
    try {
      return sessionStorage.getItem(DISMISS_KEY) ?? ''
    } catch {
      return ''
    }
  }

  private readCache (): MaintenanceState | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) {
        return null
      }
      const parsed = JSON.parse(raw)
      const state = parsed?.state
      if (!state || (state.mode !== 'off' && state.mode !== 'scheduled' && state.mode !== 'active')) {
        return null
      }
      return state as MaintenanceState
    } catch {
      return null
    }
  }

  private writeCache (state: MaintenanceState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, fetchedAt: Date.now() }))
    } catch { /* storage non disponibile */ }
  }
}

/** Guard per l'area autenticata: applica il blocco maintenance (redirect). */
export const maintenanceBlockGuard: CanActivateFn = () => {
  const maintenance = inject(MaintenanceService)
  const router = inject(Router)
  const mode = maintenance.blockMode()
  if (mode === 'none' || mode === 'overlay') {
    // overlay: la pagina si attiva ma resta coperta dal layer bloccante
    return true
  }
  maintenance.rememberReturnUrl(router.url)
  void router.navigate(['/maintenance'])
  return false
}
